/* Page 2 of a class's printed set: the character guide, drawn at the official guide's
   measured positions. The player's picks are circled among the printed options and
   their answers fill the question boxes. */

import { Fragment } from 'react';
import { LEVEL_UP_FOOTER, LEVEL_UP_TIERS, SheetSection } from './content/sheetText';
import { classTagline, Weapon } from './catalog';
import { askedQuestions, DESCRIPTION_LINES, parseTraits, TRAITS } from './model';
import { FeatureText, PageProps, weaponSummary } from './SheetParts';
import { advancementOptions } from './leveling';
import { Banner, Box, cutBox, EditGears, focusBox, Highlights, Html, PageFooter, SheetSvg, Txt } from './svg';

const AREAS: Partial<Record<SheetSection, Box>> = {
  description: [428, 60, 182, 158],
  background: [4, 222, 306, 246],
  connections: [320, 222, 288, 246],
};

// In the core guides' coordinates (the expansion guides shift them, see `shift` below).
const GEARS: Partial<Record<SheetSection, [number, number]>> = {
  description: [612, 66],
  background: [304, 234.5],
  connections: [610, 234.5],
};

const asList = (v?: string | string[] | null) => (Array.isArray(v) ? v : v ? [v] : []);

/** Splits a comma-separated pick list ("tall, toned") into its entries. */
export const picks = (text?: string) => (text ?? '').split(',').map((p) => p.trim()).filter(Boolean);

function Choices({ options, chosen }: { options: string[]; chosen: string[] }) {
  return (
    <>
      {options.map((o, i) => (
        <Fragment key={o}>
          {i > 0 && ', '}
          {chosen.includes(o) ? <mark className="cs-circled">{o}</mark> : o}
        </Fragment>
      ))}
    </>
  );
}

function OrChoice({ options, chosen }: { options: string[]; chosen: string }) {
  return (
    <>
      {options.map((o, i) => (
        <Fragment key={o}>
          {i > 0 && <strong> OR</strong>}
          {i > 0 && <br />}
          {o === chosen ? <mark className="cs-circled">{o}</mark> : o}
        </Fragment>
      ))}
    </>
  );
}

function SuggestedWeapon({ weapon }: { weapon: Weapon }) {
  return (
    <p>
      {weaponSummary(weapon)}
      {weapon.feature && <><br /><span className="cs-small"><FeatureText text={weapon.feature} /></span></>}
    </p>
  );
}

/** One question with the frame its answer is written in. */
function QuestionBox({ x0, x1, y0, y1, question, answer }: { x0: number; x1: number; y0: number; y1: number; question: string; answer?: string }) {
  return (
    <g>
      <path className="o-faint" d={`M${x0} ${y0}H${x1 - 6}L${x1} ${y0 + 6.3}V${y1 - 5.9}L${x1 - 5.6} ${y1}H${x0}`} />
      <Html x={x0} y={y0 - 2.7} w={x1 - x0 - 20} h={22} size={8} fit>
        <span className="cs-question">{question}</span>
      </Html>
      <Html x={x0 + 2} y={y0 + 16} w={x1 - x0 - 10} h={y1 - y0 - 18} className="cs-answer">{answer}</Html>
    </g>
  );
}

const TIER_X = [0, 204, 408];
const TIER_LIST_X = [24.1, 228.4, 435.2];

function Checks({ boxes, used, wide }: { boxes: number; used: number; wide?: boolean }) {
  return (
    <span className={`cs-checks${wide ? ' is-wide' : ''}`}>
      {Array.from({ length: boxes }, (_, i) => <span key={i} className={`cs-box${i < used ? ' is-marked' : ''}`} />)}
    </span>
  );
}

export default function GuidePage({ c, catalog, highlight, closeUp, onEdit }: PageProps) {
  const cls = catalog.cls(c.className);
  if (!cls) return null;
  const stats = cls.stats ?? {};
  const inv = cls.inventory;
  const traits = stats.suggestedTraits ? parseTraits(stats.suggestedTraits) : null;
  const weapons = (v?: string | string[] | null) => asList(v).map(catalog.weapon).filter((w): w is Weapon => !!w);
  const primaries = weapons(stats.suggestedPrimary);
  const secondaries = weapons(stats.suggestedSecondary);
  const armor = catalog.armor(stats.suggestedArmor ?? '');
  const desc = cls.characterDescription;
  const background = askedQuestions(cls.backgroundQuestions, c.backgroundQuestions);
  const connections = askedQuestions(cls.connectionQuestions, c.connectionQuestions);
  const bgBoxes: Array<[number, number]> = [[273.4, 327.5], [327.8, 382.7], [382.6, 433.4]];
  const marks = advancementOptions(c, 10);
  // The expansion guides (Hope & Fear, Void) are drawn 8.5pt up and left of the core guides (Aug 2025 PDFs).
  const shift = cls.source ? -8.5 : 0;

  return (
    <SheetSvg label="Character guide" className="cs-guide" view={closeUp ? focusBox(AREAS, highlight, shift) : undefined}>
      {/* Drawn in the core guides' coordinates; the expansion guides sit 8.5pt up and left of them. */}
      <g transform={shift ? `translate(${shift} ${shift})` : undefined}>
      {/* ── Header ── */}
      <rect className="s-tint2" x={0} y={0} width={629} height={53.6} />
      <path className="s-band" d="M0 0H216.8L182.9 60H0Z" />
      <rect className="s-band" x={0} y={53.6} width={629} height={1.7} />
      <Txt x={29.8} y={38.7} size={19} weight={900} tone="band">{cls.name.toUpperCase()}</Txt>
      <Txt x={30.9} y={52.6} size={11} weight={300} tone="band">CHARACTER GUIDE</Txt>
      <Html x={250} y={22.8} w={312} h={28} size={10} className="cs-center cs-italic" fit>{classTagline(cls)}</Html>

      {/* ── Suggestions, inventory, description ── */}
      <Html x={31} y={68.8} w={192} h={150} className="cs-col" fit>
        {traits && (
          <>
            <h3>Suggested Traits</h3>
            <p>{TRAITS.map((t) => `${traits[t] > 0 ? '+' : ''}${traits[t]} ${t[0].toUpperCase()}${t.slice(1)}`).join(', ')}</p>
          </>
        )}
        {primaries.length > 0 && <h3>Suggested Primary Weapon</h3>}
        {primaries.map((w) => <SuggestedWeapon key={w.name} weapon={w} />)}
        {secondaries.length > 0 && <h3>Suggested Secondary Weapon</h3>}
        {secondaries.map((w) => <SuggestedWeapon key={w.name} weapon={w} />)}
        {armor && (
          <>
            <h3>Suggested Armor</h3>
            <p>
              {armor.name} - Thresholds {armor.major}/{armor.severe} - Score {armor.score}
              {armor.feature && <><br /><span className="cs-small"><FeatureText text={armor.feature} /></span></>}
            </p>
          </>
        )}
      </Html>
      <line className="o-ink" x1={229.1} y1={79.8} x2={229.1} y2={213.4} />
      <line className="o-ink" x1={418.1} y1={79.8} x2={418.1} y2={213.4} />
      {inv && (
        <Html x={236} y={68.8} w={176} h={148} className="cs-col cs-col--center" fit>
          <h3>Inventory</h3>
          <h4>Take:</h4>
          <p>{inv.take}</p>
          <h4>Then choose between:</h4>
          <p><OrChoice options={inv.chooseBetween} chosen={c.potion} /></p>
          <h4>And either:</h4>
          <p><OrChoice options={inv.andEither} chosen={c.classItem} /></p>
          {inv.thenDecideWhatYouUseForYourCraft && (
            <>
              <h4>{inv.craftLabel ?? 'Then decide what you carry your spells in'}:</h4>
              <p>
                {inv.thenDecideWhatYouUseForYourCraft.join(', ')}
                {c.craft && <> <mark className="cs-circled">{c.craft}</mark></>}
              </p>
            </>
          )}
        </Html>
      )}
      {desc && (
        <Html x={435} y={68.8} w={172} h={150} className="cs-col" fit>
          <h3>Character Description</h3>
          <p><strong>Choose one (or more) from each line, or write your own description.</strong></p>
          {DESCRIPTION_LINES.map(({ key, label }) => {
            const chosen = picks(c.description[key]);
            const own = chosen.filter((p) => !desc[key].includes(p));
            return (
              <p key={key}>
                <strong>{label}:</strong> <Choices options={desc[key]} chosen={chosen} />
                {own.length > 0 && <>, <mark className="cs-circled">{own.join(', ')}</mark></>}
              </p>
            );
          })}
        </Html>
      )}

      {/* ── Background questions and connections ── */}
      <path className="o-frame" d={cutBox(-37, 234.5, 313, 470.6, 5.7)} />
      <path className="o-frame" d={cutBox(316.6, 234.5, 675.9, 470.8, 5.7)} />
      <Banner x0={60} x1={246.6} y={227.8} h={13.1} size={9} title="BACKGROUND QUESTIONS" tab={[106.2, 200.3]} tabRaise={2.1} wings={[54.2, 252.4]} />
      <Banner x0={378} x1={564.6} y={227.8} h={13.1} size={9} title="CONNECTIONS" tab={[424.2, 518.3]} tabRaise={2.1} wings={[372.2, 570.4]} />
      <Html x={40} y={244.5} w={252} h={22} size={8} className="cs-center cs-italic" fit>
        Answer any of the following background questions.<br />You can also create your own questions.
      </Html>
      <Html x={348} y={244.5} w={250} h={22} size={8} className="cs-center cs-italic" fit>
        Ask your fellow players one of the following questions for<br />their character to answer, or create your own questions.
      </Html>
      {bgBoxes.map(([y0, y1], i) => background[i] && (
        <QuestionBox key={`b${i}`} x0={31} x1={300.2} y0={y0} y1={y1} question={background[i]} answer={c.background[i]} />
      ))}
      {bgBoxes.map(([y0, y1], i) => connections[i] && (
        <QuestionBox key={`c${i}`} x0={338.3} x1={597.4} y0={y0} y1={y1} question={connections[i]} answer={c.connections[i]} />
      ))}
      <Html x={30.9} y={438.7} w={280} h={12} size={8} className="cs-italic">
        Then work with the GM to generate two starting Experiences for your character.
      </Html>

      {/* ── Level up, tiers 2 to 4 ── */}
      <path className="s-tint2 o-frame" d={cutBox(-18.9, 470.6, 212.9, 822.9, 8.5)} />
      <path className="s-tint2 o-frame" d={cutBox(417, 470.7, 645.8, 823, 8.5)} />
      {LEVEL_UP_TIERS.map((t, k) => {
        const dx = TIER_X[k];
        return (
          <g key={t.tier}>
            <path className="s-frame" d={cutBox(28.3 + dx, 466, 193.5 + dx, 523.8, 5.1)} />
            <Banner
              x0={26.5 + dx}
              x1={194.5 + dx}
              y={464.2}
              h={13.1}
              size={9}
              tab={[68.2 + dx, 152.8 + dx]}
              tabRaise={2.1}
              wings={[21.3 + dx, 199.7 + dx]}
              title={<><tspan>{`TIER ${t.tier}:`}</tspan><tspan fontWeight={300}>{` ${t.levels.toUpperCase()}`}</tspan></>}
            />
            <Html x={34 + dx} y={478} w={154} h={45} className="cs-tier-intro cs-flex-center">{t.intro}</Html>
            <Html x={24 + dx} y={527} w={182} h={22} size={8} className="cs-center cs-pre">{t.choose}</Html>
            <Html x={TIER_LIST_X[k]} y={554.6} w={184} h={226} className="cs-levelup" fit>
              <ul>
                {t.options.map((o, i) => {
                  const mark = marks.filter((m) => m.tier === t.tier)[i];
                  return (
                  <li key={o.text} className={[o.wide && 'is-wide', mark?.crossed && 'is-crossed'].filter(Boolean).join(' ')}>
                    <Checks boxes={o.boxes} used={mark?.used ?? 0} wide={o.wide} />
                    <span>{o.text}</span>
                  </li>
                  );
                })}
              </ul>
              <p>{LEVEL_UP_FOOTER}</p>
            </Html>
          </g>
        );
      })}

      <PageFooter hf={cls.source === 'HF'} x={26.6} />
      <Highlights boxes={AREAS} on={highlight} />
      <EditGears at={GEARS} onEdit={onEdit} />
      </g>
    </SheetSvg>
  );
}
